import { useEffect, useState } from "react";
import { api, fdayLong } from "../api";
import { useAuth } from "../auth";
import { OnboardingCard } from "../components/Onboarding";
import { ActiveWork } from "../components/home/ActiveWork";
import { Attention } from "../components/home/Attention";
import { HomeSkeleton } from "../components/home/HomeBits";
import { Performance, Pipeline } from "../components/home/Performance";
import { TodayDrawer } from "../components/home/TodayDrawer";
import { TodayStrip } from "../components/home/TodayStrip";
import { TeamToday, Upcoming } from "../components/home/Upcoming";
import { WorkspaceSwitch } from "../components/home/WorkspaceSwitch";
import { BriefCard } from "../components/intelligence/Brief";

const ACTION_LABELS = {
  "quote.signed": "Quote accepted & signed", "deposit.paid": "Deposit paid", "variation.approved": "Variation approved", "variation.rejected": "Variation rejected",
  "invoice.paid_online": "Invoice paid online", "invoice.payment_recorded": "Payment recorded", "sub.accepted": "Subcontractor accepted job", "sub.completed": "Subcontractor completed work",
  "booking.received": "New online booking", "job.created": "Job created", "job.started": "Work started on site", "labour.recorded": "Hours recorded", "quote.sent": "Quote sent",
  "invoice.sent": "Invoice sent", "variation.sent": "Variation sent", "lead.created": "New lead", "task.completed": "Task completed", "photo.uploaded": "Photos uploaded",
  "document.uploaded": "Document uploaded", "quote.declined": "Quote declined", "quote.question": "Customer asked a question", "variation.question": "Question on variation",
  "project.created": "Project created", "project.update": "Project update posted", "project.stage_changed": "Project stage changed", "milestone.completed": "Milestone completed",
  "project.issue_raised": "Issue raised", "project.issue_resolved": "Issue resolved", "variation.created": "Variation drafted", "invoice.created": "Invoice raised", "demo.provisioned": "Demo environment created",
};
export const actionLabel = (a = "") => ACTION_LABELS[a] || a.replace(/\./g, " · ").replace(/_/g, " ");

const PERIOD_KEY = "traide-home-period";

// HOME — four questions, answered in order: what needs me, how are we doing, what is happening today, where do I go next.
export default function Dashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(() => localStorage.getItem(PERIOD_KEY) || "month");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [today, setToday] = useState(false);
  const load = (p = period) => api.get("/home", { params: { period: p } }).then((r) => { setData(r.data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { load(period); localStorage.setItem(PERIOD_KEY, period); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => load(), 60000); return () => clearInterval(t); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err && !data) return <div className="card flex items-center justify-between gap-3 p-4 text-sm" data-testid="dashboard-error"><span className="t1">Couldn't load your home.</span><button className="btn-secondary btn-sm" onClick={() => load()}>Retry</button></div>;
  if (!data) return <HomeSkeleton />;
  const ws = data.workspace;
  const first = user?.name?.split(" ")[0];

  return (
    <div className="mx-auto flex max-w-[1180px] flex-col gap-4" data-testid="dashboard" data-workspace={ws}>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <div className="t3 text-[12.5px]" data-testid="home-date">{fdayLong(data.date)}</div>
          <h1 className="h1 mt-0.5" data-testid="home-greeting">{data.greeting}, {first}.</h1>
        </div>
        <WorkspaceSwitch value={ws} onChange={(w) => { setData({ ...data, workspace: w }); load(); }} />
      </div>

      <TodayStrip today={data.today} workspace={ws} onView={() => setToday(true)} />
      <BriefCard fallback={data.insight} />
      <OnboardingCard />

      {ws === "owner" && <Performance perf={data.performance} period={period} onPeriod={setPeriod} className="order-3 md:order-none" />}
      {ws === "estimating" && <Pipeline perf={data.performance} active={data.active_work} className="order-3 md:order-none" />}
      <Attention items={data.attention} other={data.attention_other} className="order-2 md:order-none" />
      {ws === "operations" && <Upcoming items={data.upcoming} title="Schedule & deliveries" className="order-4 md:order-none" />}
      {ws === "operations" && <TeamToday team={data.team} className="order-5 md:order-none" />}
      <ActiveWork active={data.active_work} className="order-6 md:order-none" />
      {ws !== "operations" && <Upcoming items={data.upcoming} title={ws === "estimating" ? "Appointments & site visits" : "Upcoming today"} className="order-7 md:order-none" />}

      <TodayDrawer open={today} onClose={() => setToday(false)} data={data} />
    </div>
  );
}
